import { CdkTable, CdkTableModule } from '@angular/cdk/table';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TooltipDirective } from '@celerofinancas/ui-directives';
import { BankUrlPipe } from '@celerofinancas/ui-pipes';
import { BANK } from '@celerofinancas/core-ts';
import { ShimmerComponent } from '../shimmer/shimmer.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import {
  CellRenderResult,
  CurrencyRenderResult,
  CurrencyTemplateContext,
  CustomTemplateContext,
  DataTableAction,
  DataTableColumn,
  DataTableConfig,
  DataTableCustomCellActionEvent,
  DataTableRow,
  DataTableSort,
  DefaultTemplateContext,
  LogoRenderResult,
  LogoTemplateContext,
  StatusRenderResult,
  StatusTemplateContext,
} from './data-table.interface';

/**
 * Reusable data table component
 */
@Component({
  selector: 'caixa-webcomponent-data-table',
  standalone: true,
  imports: [
    TooltipDirective,
    CdkTableModule,
    ShimmerComponent,
    NgTemplateOutlet,
    BankUrlPipe,
    EmptyStateComponent,
  ],
  providers: [BankUrlPipe],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent implements OnInit, OnChanges {
  /**
   * Input data for the table
   */
  @Input()
  public data: DataTableRow[] = [];
  /**
   * Table configuration
   */
  @Input()
  public config: DataTableConfig = { columns: [] };
  /**
   * Table actions configuration
   */
  @Input()
  public actions: DataTableAction[] = [];
  /**
   * Loading state flag
   */
  @Input()
  public loading = false;
  /**
   * Number of shimmer rows to display during loading
   */
  @Input()
  public shimmerRows = 5;
  /**
   * Default empty message
   */
  @Input()
  public emptyMessage = 'Nenhum dado encontrado';
  /**
   * Sort change event emitter
   */
  @Output()
  public sortChange = new EventEmitter<DataTableSort>();
  /**
   * Row click event emitter
   */
  @Output()
  public rowClick = new EventEmitter<DataTableRow>();
  /**
   * Action click event emitter
   */
  @Output()
  public actionClick = new EventEmitter<{ action: DataTableAction; row: DataTableRow }>();
  /**
   * Custom cell action click event emitter
   */
  @Output()
  public customCellActionClick = new EventEmitter<DataTableCustomCellActionEvent>();
  /**
   * CDK table view child reference
   */
  @ViewChild('cdkTable')
  public cdkTable: CdkTable<DataTableRow>;
  /**
   * Template reference for logo cells
   */
  @ViewChild('logoTemplate', { static: true })
  public logoTemplate!: TemplateRef<LogoTemplateContext>;
  /**
   * Template reference for currency cells
   */
  @ViewChild('currencyTemplate', { static: true })
  public currencyTemplate!: TemplateRef<CurrencyTemplateContext>;
  /**
   * Template reference for status cells
   */
  @ViewChild('statusTemplate', { static: true })
  public statusTemplate!: TemplateRef<StatusTemplateContext>;
  /**
   * Template reference for custom cells
   */
  @ViewChild('customTemplate', { static: true })
  public customTemplate!: TemplateRef<CustomTemplateContext>;
  /**
   * Template reference for default cells
   */
  @ViewChild('defaultTemplate', { static: true })
  public defaultTemplate!: TemplateRef<DefaultTemplateContext>;

  /**
   * Default muted color for cells without specific color configuration
   */
  private static readonly DEFAULT_MUTED_COLOR = 'var(--cds-text-light)';

  /**
   * Array of displayed column keys
   */
  public displayedColumns: string[] = [];
  /**
   * Current sorting configuration
   */
  public currentSort: DataTableSort = { column: '', direction: '' };
  /**
   * DomSanitizer for safe HTML rendering
   */
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * BankUrlPipe for resolving bank image URLs from bank codes.
   */
  private readonly bankUrlPipe = inject(BankUrlPipe);

  /**
   * FIX 86e1r18xt — trackBy estável: impede o cdk-table de recriar as linhas/botões a cada
   * novo array de dados. Sem isso, sob change detection frequente, o clique pode cair num
   * botão recém-recriado e o evento Angular se perde (modal não abre).
   */
  public trackByRow = (index: number, row: DataTableRow): unknown => {
    const item = row as Record<string, unknown> | undefined;
    return item?.['investmentId'] ?? item?.['id'] ?? index;
  };

  /**
   * Initializing the component to update the displayedColumns array based on the current table configuration and available actions.
   */
  public ngOnInit(): void {
    this.updateDisplayedColumns();
  }

  /**
   * Called when the component input properties change.
   */
  public ngOnChanges(): void {
    this.updateDisplayedColumns();
  }

  /**
   * Updates the displayedColumns array based on the current table configuration and available actions.
   */
  private updateDisplayedColumns() {
    this.displayedColumns = [
      ...this.config.columns.map((col) => col.key),
      ...(this.config.showActions && this.actions.length > 0 ? ['actions'] : []),
    ];
  }

  /**
   * Get column configuration by key
   *
   * @param key - Column key to search for
   * @returns DataTableColumn or undefined if not found
   */
  public getColumn(key: string): DataTableColumn | undefined {
    return this.config.columns.find((col) => col.key === key);
  }

  /**
   * Get column label by key
   *
   * @param key - Column key to get label for
   * @returns Column label or key if label not found
   */
  public getColumnLabel(key: string): string {
    const column = this.getColumn(key);
    return column?.label || key;
  }

  /**
   * Handle column sorting
   *
   * @param column - Column to sort by
   * @returns void
   */
  public onSort(column: DataTableColumn): void {
    if (!column.sortable) return;

    if (this.currentSort.column === column.key) {
      if (this.currentSort.direction === 'asc') {
        this.currentSort.direction = 'desc';
      } else if (this.currentSort.direction === 'desc') {
        this.currentSort.direction = '';
      } else {
        this.currentSort.direction = 'asc';
      }
    } else {
      this.currentSort.column = column.key;
      this.currentSort.direction = 'asc';
    }

    this.sortChange.emit({ ...this.currentSort });
  }

  /**
   * Get sort icon CSS class
   *
   * @param column - Column to get sort icon for
   * @returns CSS class string for sort icon
   */
  public getSortIconClass(column: DataTableColumn | undefined): string {
    if (!column || !column.sortable || this.currentSort.column !== column.key) {
      return 'cds-icon-caret-down cds-icon-sm text-muted';
    }

    if (this.currentSort.direction === 'asc') {
      return 'cds-icon-caret-up cds-icon-sm text-muted';
    } else if (this.currentSort.direction === 'desc') {
      return 'cds-icon-caret-down cds-icon-sm text-muted';
    }

    return 'cds-icon-caret-down cds-icon-sm text-muted';
  }

  /**
   * Get column CSS classes
   *
   * @param column - Column to get CSS classes for
   * @returns CSS classes string
   */
  public getColumnClass(column: DataTableColumn): string {
    const classes = ['table-cell'];

    if (column.align) {
      classes.push(`text-${column.align}`);
    }

    if (column.width) {
      classes.push('custom-width');
    }

    return classes.join(' ');
  }

  /**
   * Get header content CSS classes
   *
   * @param column - Column to get header CSS classes for
   * @returns CSS classes string
   */
  public getHeaderContentClass(column: DataTableColumn): string {
    const classes = ['header-content'];

    if (column.align) {
      classes.push(`text-${column.align}`);
    }

    return classes.join(' ');
  }

  /**
   * Render cell value as string
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns String representation of cell value
   */
  public renderCellValue(column: DataTableColumn, row: DataTableRow): string {
    const value = row[column.key];

    if (value === null || value === undefined) {
      return '-';
    }

    return String(value);
  }

  /**
   * Handle row click event
   *
   * @param row - Row that was clicked
   * @returns void
   */
  public onRowClick(row: DataTableRow): void {
    this.rowClick.emit(row);
  }

  /**
   * Handle action click event
   *
   * @param action - Action that was clicked
   * @param row - Row data
   * @param event - Click event
   * @returns void
   */
  public onActionClick(action: DataTableAction, row: DataTableRow, event: Event): void {
    event.stopPropagation();
    this.actionClick.emit({ action, row });
    action.action(row);
  }

  /**
   * Handle clicks from custom cell content actions.
   *
   * @param event click event
   * @param columnKey column key
   * @param row row data
   */
  public onCustomCellClick(event: Event, columnKey: string, row: DataTableRow): void {
    const { target } = event;

    if (!(target instanceof Element)) {
      return;
    }

    const actionElement = target.closest('[class*="custom-cell-action--"]') as HTMLElement | null;

    if (!actionElement) {
      return;
    }

    const actionClass = Array.from(actionElement.classList).find((cls) =>
      cls.startsWith('custom-cell-action--'),
    );

    if (!actionClass) {
      return;
    }

    const action = actionClass.replace('custom-cell-action--', '');

    event.stopPropagation();
    this.customCellActionClick.emit({
      action,
      columnKey,
      row,
    });
  }

  /**
   * Render cell content based on column type
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns CellRenderResult with rendered content
   */
  public renderCell(column: DataTableColumn, row: DataTableRow): CellRenderResult {
    const value = row[column.key];

    switch (column.renderType) {
      case 'currency':
        return this.renderCurrency(value, column.renderConfig);
      case 'status':
        return this.renderStatus(value, column.renderConfig);
      case 'logo':
        return this.renderLogo(value, column.renderConfig);
      case 'custom':
        return column.renderConfig?.customRenderer?.(value, row) || String(value);
      default:
        return this.renderCellValue(column, row);
    }
  }

  /**
   * Type guard for currency render result
   *
   * @param result - Cell render result to check
   * @returns boolean indicating if result is CurrencyRenderResult
   */
  public isCurrencyResult(result: CellRenderResult): result is CurrencyRenderResult {
    return (
      typeof result === 'object' &&
      result !== null &&
      'type' in result &&
      result.type === 'currency'
    );
  }

  /**
   * Type guard for status render result
   *
   * @param result - Cell render result to check
   * @returns boolean indicating if result is StatusRenderResult
   */
  public isStatusResult(result: CellRenderResult): result is StatusRenderResult {
    return (
      typeof result === 'object' && result !== null && 'type' in result && result.type === 'status'
    );
  }

  /**
   * Type guard for logo render result
   *
   * @param result - Cell render result to check
   * @returns boolean indicating if result is LogoRenderResult
   */
  public isLogoResult(result: CellRenderResult): result is LogoRenderResult {
    return (
      typeof result === 'object' && result !== null && 'type' in result && result.type === 'logo'
    );
  }

  /**
   * Get logo color for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Color string for logo
   */
  public getLogoColor(column: DataTableColumn, row: DataTableRow): string {
    const result = this.renderCell(column, row);
    if (this.isLogoResult(result)) {
      return result.color;
    }
    return DataTableComponent.DEFAULT_MUTED_COLOR;
  }

  /**
   * Get logo text for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Text string for logo
   */
  public getLogoText(column: DataTableColumn, row: DataTableRow): string {
    const result = this.renderCell(column, row);
    if (this.isLogoResult(result)) {
      return result.text;
    }
    return '-';
  }

  /**
   * Get logo tooltip for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Full text for logo tooltip
   */
  public getLogoTooltip(column: DataTableColumn, row: DataTableRow): string {
    const result = this.renderCell(column, row);
    if (!this.isLogoResult(result)) {
      return '-';
    }

    if (typeof result.rawValue === 'string') {
      const trimmedValue = result.rawValue.trim();
      return trimmedValue || result.text;
    }

    if (result.rawValue === null || result.rawValue === undefined) {
      return result.text;
    }

    return String(result.rawValue);
  }

  /**
   * Get logo bank code for image rendering.
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Bank code string for bankUrl pipe
   */
  public getLogoBankCode(column: DataTableColumn, row: DataTableRow): string {
    const rowBankCode = row[`${column.key}Code`];

    if (typeof rowBankCode === 'string' && !!rowBankCode.trim()) {
      return rowBankCode.trim();
    }

    if (typeof rowBankCode === 'number' && Number.isFinite(rowBankCode)) {
      return String(rowBankCode);
    }

    const result = this.renderCell(column, row);

    if (!this.isLogoResult(result) || result.rawValue === null || result.rawValue === undefined) {
      return '';
    }

    if (typeof result.rawValue === 'number' && Number.isFinite(result.rawValue)) {
      return String(result.rawValue);
    }

    if (typeof result.rawValue !== 'string') {
      return '';
    }

    const normalizedRawValue = result.rawValue.trim();

    return /^\d+$/.test(normalizedRawValue) ? normalizedRawValue : '';
  }

  /**
   * Checks whether logo column should render image through bankUrl pipe.
   *
   * @param column - Column configuration
   * @param row - Row data
   * @param bankCode - Pre-resolved bank code
   * @returns true when bank logo image is enabled
   */
  public showBankLogo(
    column: DataTableColumn,
    row: DataTableRow,
    bankCode?: string,
  ): boolean {
    const resolvedBankCode = bankCode ?? this.getLogoBankCode(column, row);

    return (column.renderConfig?.logoConfig?.useBankLogo ?? false) && !!resolvedBankCode;
  }

  /**
   * Get currency value for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Formatted currency value string
   */
  public getCurrencyValue(column: DataTableColumn, row: DataTableRow): string {
    const result = this.renderCell(column, row);
    if (this.isCurrencyResult(result)) {
      return result.value;
    }
    return '-';
  }

  /**
   * Get currency CSS class for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns CSS class string for currency
   */
  public getCurrencyClass(column: DataTableColumn, row: DataTableRow): string {
    const customClass = column.renderConfig?.customClass;
    if (customClass) {
      return customClass;
    }

    const result = this.renderCell(column, row);
    if (this.isCurrencyResult(result)) {
      return result.rawValue >= 0 ? 'positive' : 'negative';
    }
    return '';
  }

  /**
   * Get status text for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Status text string
   */
  public getStatusText(column: DataTableColumn, row: DataTableRow): string {
    const result = this.renderCell(column, row);
    if (this.isStatusResult(result)) {
      return result.text;
    }
    return '-';
  }

  /**
   * Get status CSS class for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns CSS class string for status
   */
  public getStatusClass(column: DataTableColumn, row: DataTableRow): string {
    const result = this.renderCell(column, row);
    if (this.isStatusResult(result)) {
      return result.class;
    }
    return 'status-default';
  }

  /**
   * Get status icon for a cell
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Status icon string or undefined
   */
  public getStatusIcon(column: DataTableColumn, row: DataTableRow): string | undefined {
    const result = this.renderCell(column, row);
    if (this.isStatusResult(result)) {
      return result.icon;
    }
    return undefined;
  }

  /**
   * Render currency value with formatting
   */
  private renderCurrency(
    value: string | number | boolean | null | undefined,
    config?: { currency?: string; showSymbol?: boolean },
  ): CurrencyRenderResult {
    if (value === null || value === undefined)
      return {
        type: 'currency',
        value: '-',
        rawValue: 0,
        showSymbol: false,
      };

    const currency = config?.currency || 'BRL';
    const showSymbol = config?.showSymbol !== false;

    const numericValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;

    const formatted = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);

    return {
      type: 'currency',
      value: formatted,
      rawValue: numericValue,
      showSymbol,
    };
  }

  /**
   * Render status value with mapping
   */
  private renderStatus(
    value: string | number | boolean | null | undefined,
    config?: { statusMap?: Record<string, { text: string; class: string; icon?: string }> },
  ): StatusRenderResult {
    if (value === null || value === undefined)
      return {
        type: 'status',
        text: '-',
        class: 'status-default',
        rawValue: value,
      };

    const statusMap = config?.statusMap || {};
    const stringValue = String(value);
    const status = statusMap[stringValue] || { text: stringValue, class: 'status-default' };

    return {
      type: 'status',
      text: status.text,
      class: status.class,
      icon: status.icon,
      rawValue: value,
    };
  }

  /**
   * Render logo value with color and text mapping
   */
  private renderLogo(
    value: string | number | boolean | null | undefined,
    config?: {
      logoConfig?: {
        colorMap?: Record<string, string>;
        textMap?: Record<string, string>;
        defaultColor?: string;
      };
    },
  ): LogoRenderResult {
    if (value === null || value === undefined)
      return {
        type: 'logo',
        color: DataTableComponent.DEFAULT_MUTED_COLOR,
        text: '-',
        rawValue: value,
      };

    const logoConfig = config?.logoConfig || {};
    const colorMap = logoConfig.colorMap || {};
    const textMap = logoConfig.textMap || {};
    const defaultColor = logoConfig.defaultColor || DataTableComponent.DEFAULT_MUTED_COLOR;
    const stringValue = String(value);

    const color = colorMap[stringValue] || defaultColor;
    const text = textMap[stringValue] || stringValue.substring(0, 2).toUpperCase();

    return {
      type: 'logo',
      color,
      text,
      rawValue: value,
    };
  }

  /**
   * Get cell color based on configuration
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Color string for cell
   */
  public getCellColor(column: DataTableColumn, row: DataTableRow): string {
    const value = row[column.key];
    if (value === null || value === undefined)
      return column.renderConfig?.defaultColor || DataTableComponent.DEFAULT_MUTED_COLOR;

    const colorMap = column.renderConfig?.colorMap;
    if (colorMap && typeof value === 'string') {
      return (
        colorMap[value] ||
        column.renderConfig?.defaultColor ||
        DataTableComponent.DEFAULT_MUTED_COLOR
      );
    }

    return column.renderConfig?.defaultColor || DataTableComponent.DEFAULT_MUTED_COLOR;
  }

  /**
   * Get cell text based on configuration
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Text string for cell
   */
  public getCellText(column: DataTableColumn, row: DataTableRow): string {
    const value = row[column.key];
    if (value === null || value === undefined) return column.renderConfig?.defaultText || '-';

    const textMap = column.renderConfig?.textMap;
    if (textMap && typeof value === 'string') {
      return textMap[value] || String(value);
    }

    return String(value);
  }

  /**
   * Get cell CSS class based on configuration
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns CSS class string for cell
   */
  public getCellClass(column: DataTableColumn, row: DataTableRow): string {
    const value = row[column.key];
    if (value === null || value === undefined) return column.renderConfig?.defaultClass || '';

    const classMap = column.renderConfig?.classMap;
    if (classMap && typeof value === 'string') {
      return classMap[value] || column.renderConfig?.defaultClass || '';
    }

    return column.renderConfig?.defaultClass || '';
  }

  /**
   * Get cell icon based on configuration
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Icon string or undefined
   */
  public getCellIcon(column: DataTableColumn, row: DataTableRow): string | undefined {
    const value = row[column.key];
    if (value === null || value === undefined) return undefined;

    const iconMap = column.renderConfig?.iconMap;
    if (iconMap && typeof value === 'string') {
      return iconMap[value];
    }

    return undefined;
  }

  /**
   * Gets custom content layout style for a column.
   *
   * @param column column configuration
   * @returns custom layout style
   */
  public getCustomLayout(column: DataTableColumn): 'default' | 'between' {
    return column.renderConfig?.customLayout === 'between' ? 'between' : 'default';
  }

  /**
   * Get template context for dynamic rendering
   *
   * @param column - Column configuration
   * @param row - Row data
   * @returns Template context object
   */
  public getTemplateContext(column: DataTableColumn, row: DataTableRow) {
    const renderType = column.renderType || 'default';

    switch (renderType) {
      case 'logo': {
        const imageUrlKey = column.renderConfig?.logoConfig?.imageUrlKey;
        const imageUrl = imageUrlKey ? (row[imageUrlKey] as string | undefined) : undefined;
        const bankCode = this.showBankLogo(column, row)
          ? this.getLogoBankCode(column, row)
          : undefined;
        return {
          type: 'logo',
          color: this.getLogoColor(column, row),
          text: this.getLogoText(column, row),
          tooltip: this.getLogoTooltip(column, row),
          imageUrl,
          bankCode,
        };
      }
      case 'currency':
        return {
          type: 'currency',
          value: this.getCurrencyValue(column, row),
          class: this.getCurrencyClass(column, row),
        };
      case 'status':
        return {
          type: 'status',
          text: this.getStatusText(column, row),
          class: this.getStatusClass(column, row),
          icon: this.getStatusIcon(column, row),
        };
      case 'custom':
        return {
          type: 'custom',
          content: String(this.renderCell(column, row)),
          row,
          columnKey: column.key,
          layout: this.getCustomLayout(column),
        };
      default:
        return {
          type: 'default',
          text: this.getCellText(column, row),
          class: this.getCellClass(column, row),
          icon: this.getCellIcon(column, row),
        };
    }
  }

  /**
   * Get the appropriate template for a render type
   *
   * @param renderType - Type of render to get template for
   * @returns TemplateRef for the render type
   */
  public getCellTemplate(renderType: string): TemplateRef<unknown> {
    switch (renderType) {
      case 'logo':
        return this.logoTemplate;
      case 'currency':
        return this.currencyTemplate;
      case 'status':
        return this.statusTemplate;
      case 'custom':
        return this.customTemplate;
      default:
        return this.defaultTemplate;
    }
  }

  /**
   * Sanitize HTML content
   *
   * @param content - HTML content to sanitize
   * @returns SafeHtml sanitized content
   */
  public sanitizeContent(content: string): SafeHtml {
    return this.sanitizer.sanitize(1, content) || '';
  }

  /**
   * Handles logo image load error by hiding the broken image and rendering
   * a text fallback directly in the DOM.
   *
   * @param event - Error event from the img element
   * @param color - Background color to apply to the logo circle
   * @param name - Institution name used to derive the 2-char text fallback
   */
  public handleLogoError(event: Event, color: string, name: string): void {
    const img = event.target;

    if (!(img instanceof HTMLImageElement)) {
      return;
    }

    const circle = img.closest('.logo-circle') as HTMLElement | null;

    if (!circle) {
      return;
    }

    img.style.display = 'none';
    circle.style.backgroundColor = color;

    const span = document.createElement('span');
    span.className = 'logo-text';
    span.textContent = name.slice(0, 2).toUpperCase();
    circle.appendChild(span);
  }

  /**
   * Resolves a bank image URL from a bank code using the BankUrlPipe.
   *
   * @param bankCode - Bank code to resolve
   * @returns Resolved bank image URL
   */
  public resolveLogoUrl(bankCode: string): string {
    return this.bankUrlPipe.transform(bankCode as unknown as BANK);
  }
}
